package com.evoapps.evoshift.session

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.model.Roster
import com.evoapps.evoshift.domain.model.RosterIds
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.YearMonth
import kotlinx.datetime.minusMonth
import kotlinx.datetime.number
import kotlinx.datetime.plusMonth
import kotlinx.datetime.todayIn
import kotlinx.datetime.yearMonth

/**
 * SH-89: the workplace and the month the app shows, one choice for every screen (the dashboard,
 * the roster, the daily records, …): switching the month on one screen carries over to the
 * others. The workplace stays owned (and remembered across restarts) by [session]; the month
 * starts at the current one and lives as long as the app (a restart opens on "now").
 *
 * Links and notifications set both through [openAt] / [openRoster].
 */
class WorkplaceMonthSelection(
    private val session: WorkplaceSessionManager,
    private val today: () -> LocalDate = { AppClock.todayIn(TimeZone.currentSystemDefault()) }
) {
    private val _month = MutableStateFlow(today().yearMonth)

    /** The chosen month; the current month until someone picks another. */
    val month: StateFlow<YearMonth> = _month.asStateFlow()

    /** The chosen workplace (the session's). */
    val workplaceId: StateFlow<String?> get() = session.currentWorkplaceId

    /** The current month, for "This month" in the picker. */
    val currentMonth: YearMonth get() = today().yearMonth

    fun selectMonth(month: YearMonth) {
        _month.value = month
    }

    fun previousMonth() = selectMonth(_month.value.minusMonth())

    fun nextMonth() = selectMonth(_month.value.plusMonth())

    /** Another workplace; the month stays ("October at Port" after "October at Aurora"). */
    fun selectWorkplace(workplaceId: String) = session.setWorkplaceId(workplaceId)

    /** A link or a notification: its workplace and month, either may be absent. */
    fun openAt(workplaceId: String? = null, month: YearMonth? = null) {
        if (workplaceId != null && workplaceId != session.currentWorkplaceId.value) session.setWorkplaceId(workplaceId)
        if (month != null) selectMonth(month)
    }

    /** A screen opened on a roster (a link, a card, a notification): its workplace and month. */
    fun openRoster(rosterId: String) = openAt(RosterIds.workplaceIdOf(rosterId), monthOf(rosterId))

    /** Signing out: the next person starts on the current month. */
    fun reset() {
        _month.value = today().yearMonth
    }

    companion object {
        fun monthOf(rosterId: String): YearMonth? =
            runCatching { RosterIds.yearMonthOf(rosterId).let { (year, month) -> YearMonth(year, month) } }.getOrNull()
    }
}

/** The roster's month. */
val Roster.yearMonth: YearMonth get() = YearMonth(year, month)

/** The roster of [month] among these, if there is one. */
fun List<Roster>.of(month: YearMonth): Roster? = firstOrNull { it.year == month.year && it.month == month.month.number }
